import { ComponentProps } from "react";

export type RadioOptionBase = {
  value: string | number;
  label: string;
  description?: string;
  disabled?: boolean;
};

export type Props = Omit<ComponentProps<"div">, "onChange"> & {
  /** Field name for identification */
  name: string;
  /** Available options */
  options: RadioOptionBase[];
  /** Currently selected value(s) - array for multi-select */
  value?: (string | number)[];
  /** Change handler - receives array of selected values */
  onChange?: (value: (string | number)[]) => void;
  /** Blur handler */
  onBlur?: () => void;
  /** Whether the field has an error (for styling and aria-invalid) */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Maximum number of items that can be selected (default: 1) */
  maxItems?: number;
  /** ID of the element that describes this group (typically error message) */
  "aria-describedby"?: string;
  /** Called when the user tries to select more items than maxItems allows */
  onMaxItemsExceeded?: (maxItems: number) => void;
  /** Render function for each option */
  renderOption?: (props: {
    option: RadioOptionBase;
    isChecked: boolean;
    isDisabled: boolean;
    onChange: (checked: boolean) => void;
  }) => React.ReactNode;
};

/**
 * Base radio group component with no styling.
 * Use this as the foundation for styled radio group components.
 *
 * Supports both single-select (maxItems=1) and multi-select (maxItems>1) modes.
 */
export function RadioGroupBase({
  name,
  options,
  value = [],
  onChange,
  onBlur,
  error,
  disabled = false,
  maxItems = 1,
  onMaxItemsExceeded,
  renderOption,
  className,
  id,
  ...props
}: Props) {
  const isMultiSelect = maxItems > 1;
  const selectedValues = Array.isArray(value) ? value : [];

  const handleOptionChange = (
    optionValue: string | number,
    checked: boolean
  ) => {
    let newValue: (string | number)[];

    if (checked) {
      if (isMultiSelect) {
        if (selectedValues.length >= maxItems) {
          onMaxItemsExceeded?.(maxItems);
          return;
        }
        newValue = [...selectedValues, optionValue];
      } else {
        newValue = [optionValue];
      }
    } else {
      newValue = selectedValues.filter((val) => val !== optionValue);
    }

    onChange?.(newValue);
  };

  // Default render if no custom render function provided
  const defaultRenderOption = ({
    option,
    isChecked,
    isDisabled,
    onChange: onOptionChange,
  }: {
    option: RadioOptionBase;
    isChecked: boolean;
    isDisabled: boolean;
    onChange: (checked: boolean) => void;
  }) => (
    <label key={option.value}>
      <input
        type={isMultiSelect ? "checkbox" : "radio"}
        name={name}
        value={option.value}
        checked={isChecked}
        disabled={isDisabled}
        onChange={(e) => onOptionChange(e.target.checked)}
      />
      {option.label}
    </label>
  );

  const render = renderOption ?? defaultRenderOption;

  return (
    <div
      role="group"
      aria-labelledby={id ? `${id}-label` : undefined}
      aria-invalid={error || undefined}
      className={className}
      onBlur={onBlur}
      data-testid={`radiogroup-${name}`}
      {...props}
    >
      {options.map((option) => {
        const isChecked = selectedValues.includes(option.value);
        const isDisabled = disabled || !!option.disabled;

        return render({
          option,
          isChecked,
          isDisabled,
          onChange: (checked) => handleOptionChange(option.value, checked),
        });
      })}
    </div>
  );
}

RadioGroupBase.displayName = "RadioGroupBase";

